import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    ListItemText,
    MenuItem,
    Select,
    TextField,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isIntegerInRange, isPositiveInteger } from "./validation";
import { DIALOG_LABEL_WIDTH } from "./dialogLayout";
import { removeBox, splitTopologyItems } from "./siteTopologyEditing";

const SIZE_PRESETS = ["3x5", "5x5", "3x10", "5x10", "10x10"];
const CUSTOM_SIZE = "custom";
const MAX_BOXES_PER_ADD = 500;

function parseSize(value: string | undefined): { sizeOption: string; customHeight: string; customWidth: string } {
    if (value && SIZE_PRESETS.includes(value)) {
        return { sizeOption: value, customHeight: "", customWidth: "" };
    }
    const match = value?.match(/^(\d+)x(\d+)$/);
    if (match) {
        return { sizeOption: CUSTOM_SIZE, customHeight: match[1], customWidth: match[2] };
    }
    return { sizeOption: SIZE_PRESETS[0], customHeight: "", customWidth: "" };
}

function nextBoxNumber(existingItems: Record<string, SiteCommissioningItem>): string {
    const boxNumbers = Object.keys(existingItems)
        .filter((key) => key.startsWith("box:"))
        .map((key) => Number(key.substring("box:".length)))
        .filter((value) => Number.isInteger(value) && value > 0);
    return String(boxNumbers.length > 0 ? Math.max(...boxNumbers) + 1 : 1);
}

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    existingItems: Record<string, SiteCommissioningItem>;
    sensorOptions: { key: string; name: string }[];
    editingKey?: string;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

export const EditBoxDialog: React.FC<Props> = ({ open, onClose, siteId, existingItems, sensorOptions, editingKey, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [number, setNumber] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");
    const [location, setLocation] = useState("");
    const [sizeOption, setSizeOption] = useState<string>(SIZE_PRESETS[0]);
    const [customHeight, setCustomHeight] = useState("");
    const [customWidth, setCustomWidth] = useState("");
    const [sensors, setSensors] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editingKey);
    const isRange = !isEditing && rangeEnd.trim() !== "";

    useEffect(() => {
        if (open) {
            const fields = editingKey ? existingItems[editingKey]?.fields : undefined;
            const parsedSize = parseSize(typeof fields?.size === "string" ? fields.size : undefined);
            setNumber(editingKey ? editingKey.substring("box:".length) : nextBoxNumber(existingItems));
            setRangeEnd("");
            setLocation(typeof fields?.location === "string" ? fields.location : "");
            setSizeOption(parsedSize.sizeOption);
            setCustomHeight(parsedSize.customHeight);
            setCustomWidth(parsedSize.customWidth);
            setSensors(Array.isArray(fields?.sensors) ? (fields.sensors as string[]) : []);
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingKey]);

    const save = () => {
        const clientErrors: string[] = [];
        if (!isPositiveInteger(number)) {
            clientErrors.push("Box number must be a positive whole number.");
        } else if (rangeEnd && (!isPositiveInteger(rangeEnd) || Number(rangeEnd) < Number(number))) {
            clientErrors.push("Range end must be a whole number no lower than the box number.");
        } else if (rangeEnd && Number(rangeEnd) - Number(number) + 1 > MAX_BOXES_PER_ADD) {
            clientErrors.push(`A range can add at most ${MAX_BOXES_PER_ADD} boxes at a time.`);
        }
        if (!location.trim()) {
            clientErrors.push("Box location is required.");
        }

        let size = sizeOption;
        if (sizeOption === CUSTOM_SIZE) {
            if (!customHeight || !customWidth) {
                clientErrors.push("Custom height and width are required.");
            } else if (!isIntegerInRange(customHeight, 1, 100) || !isIntegerInRange(customWidth, 1, 100)) {
                clientErrors.push("Custom height and width must be whole numbers between 1 and 100.");
            } else {
                size = `${customHeight}x${customWidth}`;
            }
        }

        if (clientErrors.length > 0) {
            setErrors(clientErrors);
            return;
        }

        const firstNumber = Number(number);
        const lastNumber = rangeEnd ? Number(rangeEnd) : firstNumber;
        const newKeys = Array.from({ length: lastNumber - firstNumber + 1 }, (_, index) => `box:${firstNumber + index}`);
        let items = editingKey && !newKeys.includes(editingKey) ? removeBox(existingItems, editingKey) : { ...existingItems };

        const takenNumbers = newKeys
            .filter((key) => key !== editingKey && items[key])
            .map((key) => key.substring("box:".length));
        if (takenNumbers.length === 1) {
            setErrors([`Box ${takenNumbers[0]} already exists.`]);
            return;
        }
        if (takenNumbers.length > 1) {
            setErrors([`Boxes ${takenNumbers.join(", ")} already exist.`]);
            return;
        }

        items = {
            ...items,
            ...Object.fromEntries(newKeys.map((key) => [key, { fields: { location: location.trim(), size, sensors: isRange ? [] : sensors } }])),
        };

        setSaving(true);
        const { ioDeviceItems, boxItems } = splitTopologyItems(items);
        siteApi
            .updateCommissioning(siteId, [
                { name: "ioDevices", items: ioDeviceItems },
                { name: "boxes", items: boxItems },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar(isEditing ? "Box updated" : newKeys.length > 1 ? `${newKeys.length} boxes added` : "Box added", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to save the box", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText={isEditing ? "Edit Box" : "Add Box"}>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Number" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={number}
                                onChange={(event) => setNumber(event.currentTarget.value)}
                                variant="outlined"
                                disabled={isEditing}
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    {!isEditing && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Range End">
                            <FormControl style={{ flexGrow: 1 }}>
                                <TextField
                                    value={rangeEnd}
                                    onChange={(event) => setRangeEnd(event.currentTarget.value)}
                                    variant="outlined"
                                    placeholder="Optional"
                                    InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                />
                            </FormControl>
                        </FormField>
                    )}
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Location" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={location}
                                onChange={(event) => setLocation(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Size" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <Select
                                value={sizeOption}
                                onChange={(event) => setSizeOption(event.target.value as string)}
                                variant="outlined"
                            >
                                {SIZE_PRESETS.map((preset) => (
                                    <MenuItem key={preset} value={preset}>{preset}</MenuItem>
                                ))}
                                <MenuItem value={CUSTOM_SIZE}>Custom</MenuItem>
                            </Select>
                        </FormControl>
                    </FormField>
                    {sizeOption === CUSTOM_SIZE && (
                        <>
                            <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Custom Height" required>
                                <FormControl style={{ flexGrow: 1 }}>
                                    <TextField
                                        value={customHeight}
                                        onChange={(event) => setCustomHeight(event.currentTarget.value.replace(/\D/g, ""))}
                                        variant="outlined"
                                        InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                    />
                                </FormControl>
                            </FormField>
                            <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Custom Width" required>
                                <FormControl style={{ flexGrow: 1 }}>
                                    <TextField
                                        value={customWidth}
                                        onChange={(event) => setCustomWidth(event.currentTarget.value.replace(/\D/g, ""))}
                                        variant="outlined"
                                        InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                    />
                                </FormControl>
                            </FormField>
                        </>
                    )}
                    {!isRange && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Sensors">
                            <FormControl style={{ flexGrow: 1 }}>
                                <Select
                                    multiple
                                    value={sensors}
                                    onChange={(event) => setSensors(event.target.value as string[])}
                                    renderValue={(selected) =>
                                        (selected as string[])
                                            .map((key) => sensorOptions.find((o) => o.key === key)?.name ?? key)
                                            .join(", ")
                                    }
                                    variant="outlined"
                                >
                                    {sensorOptions.map((option) => (
                                        <MenuItem key={option.key} value={option.key}>
                                            <Checkbox checked={sensors.includes(option.key)} color="primary" />
                                            <ListItemText primary={option.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormField>
                    )}
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
